<template>
	<div class="toolbar__wrapper">
		<div class="toolbar__controls">
			<div class="toolbar__title md title-default hydrated">
				{{ $t('WEBAPP_GENRES') }}
			</div>
			<div class="toolbar__buttons">
				<ResetButton :filterNames="['genres', 'exclude_genres']" />
			</div>
		</div>

		<div class="columns">
			<ToggleButton
				v-for="genre in genres"
				:key="genre.id"
				:active="isFilterCurrentlyActive('genres', genre.shortName)"
				:negative="isFilterCurrentlyActive('exclude_genres', genre.shortName)"
				:icon="isFilterCurrentlyActive('exclude_genres', genre.shortName) ? 'minus-circle' : 'jw-check-icon'"
				@click.prevent="genreClicked(genre)"
			>
				{{ genre.translation }}
			</ToggleButton>
		</div>

		<div style="margin-bottom: 18px"></div>
	</div>
</template>

<script setup lang="ts">
import type { Genre } from '@/constants/types';
import type { CollectionType } from '@/enums/collection-type';
import type { FilterCollection, FilterName } from '@/stores/useFilterStoreTypes';

import ResetButton from '@/components/buttons/ResetButton.vue';
import ToggleButton from '@/ui-components/ToggleButton.vue';

import { toastSuccess } from '@/helpers/vfm/toast-helper';
import { isFilterActive } from '@/helpers/filter-helper';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { storeToRefs } from 'pinia';
import { useConstantStore } from '@/stores/pinia/useConstantStore';

const props = defineProps<{
	collectionType: CollectionType;
	currentFilters: FilterCollection;
}>();

const { genres } = storeToRefs(useConstantStore());

function isFilterCurrentlyActive(filterName: FilterName, value: any) {
	return isFilterActive(filterName, value, props.currentFilters);
}

const { t } = useI18n();
const { toggle } = useFilterStore();
function genreClicked(genre: Genre) {
	const genreShortName = genre.shortName;
	const isIncluded = isFilterCurrentlyActive('genres', genreShortName);
	const isExcluded = isFilterCurrentlyActive('exclude_genres', genreShortName);

	if (!isIncluded && !isExcluded) {
		// neutral -> transition to included
		toggle({
			filterName: 'genres',
			value: genreShortName,
			selectedState: 1,
		});
	} else if (isIncluded) {
		// included -> transition to excluded
		toggle({
			filterName: 'genres',
			value: genreShortName,
			selectedState: 0,
			trackEvent: false,
		});
		toggle({
			filterName: 'exclude_genres',
			value: genreShortName,
			selectedState: 1,
		});

		toastSuccess({ message: t('WEBAPP_EXCLUDE_X_GENRE', { genre: genre.translation }).toString() });
	} else if (isExcluded) {
		// excluded -> transition to neutral
		toggle({
			filterName: 'exclude_genres',
			value: genreShortName,
			selectedState: 0,
		});
	}
}
</script>

<style lang="scss" scoped>
.toolbar__title {
	padding-inline: 10px;
	font-size: 20px;
	font-weight: 500;
	letter-spacing: 0.0125em;
	margin: 20px 0px;
}
.toolbar__wrapper {
	display: flex;
	flex-direction: column;
	padding: 0 15px;
}
.toolbar__controls {
	display: flex;
	align-items: center;
	justify-content: space-between;
}
.columns {
	display: grid;
	grid-template-columns: 50% 50%;
	column-gap: 10px;

	.toggle-button {
		margin-inline-end: 0;
		transform: translateZ(0);
	}
}
</style>
